import React from "react";
import AudioPlayerRewind from "./AudioPlayer";
import forwardIcon from "../../assets/images/forward.svg";
import styles from "./MultiAudioPlayer.module.css";

interface AudioOption {
    id: string;
    title: string;
    url: string | null;
}

interface MultiAudioPlayerProps {
    audioOptions: AudioOption[];
    selectedAudioId: string | null;
    onAudioSelect: (audioId: string) => void;
    onClickAddToProject?: () => void;
}

const MultiAudioPlayer: React.FC<MultiAudioPlayerProps> = ({
    audioOptions,
    selectedAudioId,
    onAudioSelect,
    onClickAddToProject,
}) => {
    // Filter to only show options with URLs (ready to play)
    const availableOptions = audioOptions.filter((option) => option.url);

    if (availableOptions.length === 0) {
        return null;
    }

    // Find current audio option
    const currentIndex = availableOptions.findIndex((opt) => opt.id === selectedAudioId);
    const currentAudio = currentIndex >= 0 ? availableOptions[currentIndex] : availableOptions[0];
    const hasNext = currentIndex < availableOptions.length - 1;
    const hasPrevious = currentIndex > 0;

    const handleNext = () => {
        if (hasNext) {
            const nextAudio = availableOptions[currentIndex + 1];
            onAudioSelect(nextAudio.id);
        }
    };

    const handlePrevious = () => {
        if (hasPrevious) {
            const prevAudio = availableOptions[currentIndex - 1];
            onAudioSelect(prevAudio.id);
        }
    };

    // If only one audio, just show the regular player
    if (availableOptions.length === 1) {
        return (
            <AudioPlayerRewind
                title={currentAudio.title}
                src={currentAudio.url || ""}
                onClickAddToProject={onClickAddToProject}
            />
        );
    }

    return (
        <div className={styles.multiAudioPlayerWrapper}>
            <AudioPlayerRewind
                title={currentAudio.title}
                src={currentAudio.url || ""}
                onClickAddToProject={onClickAddToProject}
            />
            <div className={styles.multiAudioPlayerControls}>
                <button
                    type="button"
                    className={styles.multiAudioNavButton}
                    onClick={handlePrevious}
                    disabled={!hasPrevious}
                    title="Previous audio"
                >
                    <img
                        src={forwardIcon}
                        alt="Previous"
                        style={{
                            width: "18px",
                            height: "18px",
                            transform: "scaleX(-1)",
                        }}
                    />
                </button>
                <div className={styles.multiAudioPlayerCounterWrapper}>
                    <div className={styles.multiAudioPlayerCounter}>
                        {currentIndex + 1} / {availableOptions.length}
                    </div>
                </div>
                <button
                    type="button"
                    className={styles.multiAudioNavButton}
                    onClick={handleNext}
                    disabled={!hasNext}
                    title="Next audio"
                >
                    <img
                        src={forwardIcon}
                        alt="Next"
                        style={{
                            width: "18px",
                            height: "18px",
                        }}
                    />
                </button>
            </div>
        </div>
    );
};

export default MultiAudioPlayer;
